import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { setStoredToken, updateDocument } from '../hooks/useSupabase';
import toast from 'react-hot-toast';
import PasswordInput from '../components/PasswordInput';

const LoginPage = ({ onAdminLogin, onDonorLogin }) => {
  const [loginType, setLoginType] = useState('donor'); // 'donor' or 'admin'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    adminId: null,
    newPassword: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();

  const checkAdminPasswordChangeRequired = async (adminEmail) => {
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/admins?email=eq.${encodeURIComponent(adminEmail)}&select=*`;
      const response = await fetch(url, {
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        }
      });
      if (!response.ok) return null;
      const admins = await response.json();
      if (admins && admins.length > 0) {
        return admins[0].force_password_change ? admins[0] : null;
      }
      return null;
    } catch (err) {
      console.error('Error checking admin password change:', err);
      return null;
    }
  };

  // Check if admin is using temp password
  const checkTempPassword = async (adminEmail, password) => {
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/admins?email=eq.${encodeURIComponent(adminEmail)}&select=*`;
      const response = await fetch(url, {
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        }
      });
      if (!response.ok) return null;
      const admins = await response.json();
      if (admins && admins.length > 0) {
        const admin = admins[0];
        // If temp_password is set and matches entered password
        if (admin.force_password_change && admin.temp_password === password) {
          return admin;
        }
      }
      return null;
    } catch (err) {
      console.error('Error checking temp password:', err);
      return null;
    }
  };

  const lookupDonorByEmailPhone = async (email, phone) => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/donors`;
    const lookupUrl = `${url}?email=eq.${encodeURIComponent(email)}&phone_number=eq.${encodeURIComponent(phone)}&select=*`;

    const response = await fetch(lookupUrl, {
      headers: {
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      }
    });

    if (!response.ok) return null;
    const data = await response.json();
    return data && data.length > 0 ? data[0] : null;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (loginType === 'donor') {
        // Donor login with email + phone
        if (!email || !phoneNumber) {
          throw new Error('Please enter both email and phone number');
        }

        const donor = await lookupDonorByEmailPhone(email, phoneNumber);
        if (!donor) {
          throw new Error('No donor found with this email and phone number. Please check your details or register.');
        }

        // Donor found - log them in
        toast.success('Welcome back, donor! 🎉');
        if (onDonorLogin) await onDonorLogin(donor);
        navigate(`/donor-profile?id=${donor.id}&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phoneNumber)}`);
      } else {
        // Admin login with email + password
        // Step 1: Check if admin is using temp password (password reset flow)
        const tempAdmin = await checkTempPassword(email, password);
        if (tempAdmin) {
          // Pass admin data directly since we already have it
          const isAdmin = await onAdminLogin(email, tempAdmin);
          if (!isAdmin) throw new Error('You do not have admin access. Contact the super admin.');
          
          // Show password change modal
          toast('Please set a new password to continue.');
          setPasswordChangeData({ adminId: tempAdmin.id, newPassword: '', confirmPassword: '' });
          setShowPasswordChange(true);
          setLoading(false);
          return;
        }

        // Step 2: Normal Supabase Auth login
        const testUrl = `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/token?grant_type=password`;
        try {
          const testResponse = await fetch(testUrl, {
            method: 'POST',
            headers: {
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email, password })
          });
          if (!testResponse.ok) {
            throw new Error(`Invalid email or password. Please check and try again.`);
          }
          const authData = await testResponse.json();
          if (authData.access_token) setStoredToken(authData.access_token);
        } catch (fetchErr) {
          throw new Error('Invalid email or password. Please check and try again.');
        }
        // Verify admin access
        const isAdmin = await onAdminLogin(email);
        if (!isAdmin) throw new Error('You do not have admin access. Contact the super admin.');

        // Check if password change is required (edge case)
        const adminRecord = await checkAdminPasswordChangeRequired(email);
        if (adminRecord) {
          toast('You must change your password before continuing.');
          setPasswordChangeData({ adminId: adminRecord.id, newPassword: '', confirmPassword: '' });
          setShowPasswordChange(true);
          setLoading(false);
          return;
        }

        toast.success('Welcome back, Admin! 🎉');
        navigate('/admin');
      }
    } catch (err) {
      console.error('[AdminLogin] Full error:', err);
      const msg =
        err.status === 401 || err.message?.includes('Invalid login') || err.message?.includes('401') 
          ? 'Invalid email or password. Please check and try again.' :
        err.message?.includes('Email not confirmed') 
          ? 'Please confirm your email before logging in.' :
        err.message?.includes('timeout') 
          ? 'Request timed out. Please check your connection.' :
        err.message || 'Login failed. Please check your credentials.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordChangeData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // 1. Try to update password in Supabase Auth if currently signed in
      let authSuccess = false;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { error: updateError } = await supabase.auth.updateUser({
            password: passwordChangeData.newPassword
          });
          if (!updateError) {
            authSuccess = true;
          } else {
            console.warn('Update user password error:', updateError.message);
          }
        }
      } catch (err) {
        console.error('Session/Update check failed:', err);
      }

      // 2. If not signed in or update failed, try to sign in with temp password first, then update
      if (!authSuccess) {
        try {
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: email,
            password: password
          });
          if (!signInError && signInData.session) {
            setStoredToken(signInData.session.access_token);
            const { error: updateError } = await supabase.auth.updateUser({
              password: passwordChangeData.newPassword
            });
            if (!updateError) {
              authSuccess = true;
            }
          }
        } catch (err) {
          console.error('Sign in with temp password failed:', err);
        }
      }

      // 3. If still not updated (e.g. user does not exist in Supabase Auth), try to signUp
      if (!authSuccess) {
        try {
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: email,
            password: passwordChangeData.newPassword
          });
          if (!signUpError) {
            authSuccess = true;
            if (signUpData.session) {
              setStoredToken(signUpData.session.access_token);
            }
          } else {
            console.warn('SignUp failed:', signUpError.message);
          }
        } catch (err) {
          console.error('SignUp failed:', err);
        }
      }

      // 4. Update the admins table record
      await updateDocument('admins', passwordChangeData.adminId, {
        temp_password: null,
        force_password_change: false,
        password_changed_at: new Date().toISOString()
      });

      // 5. Save email to localStorage
      localStorage.setItem('adminEmail', email.toLowerCase().trim());

      // 6. Establish active session with the new password
      try {
        const { data: finalSignIn, error: finalError } = await supabase.auth.signInWithPassword({
          email: email,
          password: passwordChangeData.newPassword
        });
        if (!finalError && finalSignIn.session) {
          setStoredToken(finalSignIn.session.access_token);
        }
      } catch (err) {
        console.error('Final sign in failed:', err);
      }

      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      navigate('/admin');
    } catch (err) {
      console.error('Password change error:', err);
      const errorMsg = err?.message || err?.error || 'Unknown error';
      toast.error(`Failed to change password: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left panel (decorative – hidden on mobile) ── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero" />
        <div className="absolute inset-0 bg-dot-pattern" />
        {/* Glow */}
        <div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 40% 60%, rgba(244,63,94,0.3) 0%, transparent 60%)' }} />

        <div className="relative flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src="/ocym-logo.png" alt="OCYM Logo" className="w-10 h-10 object-contain drop-shadow-lg" />
            <div>
              <div className="text-white font-bold text-lg leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>OCYM Raktadhan</div>
              <div className="text-crimson-200 text-xs">Donors Network</div>
            </div>
          </Link>

          {/* Center content */}
          <div className="text-center">
            <div className="mb-8 animate-float flex justify-center">
              <img src="/ocym-logo.png" alt="OCYM Logo" className="w-32 h-32 object-contain drop-shadow-2xl" />
            </div>
            <h2 className="text-3xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>
              Every Login is a<br />Step Closer to<br />Saving a Life
            </h2>
            <p className="text-crimson-200 text-lg">Join the OCYM Blood Donors Network — Kerala's trusted blood donation platform.</p>
          </div>

          {/* Bottom stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { n: '14', l: 'Districts' },
              { n: '3', l: 'Lives/donation' },
              { n: '24/7', l: 'Support' },
            ].map(({ n, l }) => (
              <div key={l} className="stat-glass py-4">
                <div className="text-2xl font-black text-white">{n}</div>
                <div className="text-crimson-200 text-xs mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel (form) ── */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">🔐 Change Password</h2>
            <p className="text-gray-600 mb-6">
              Your password was reset by an admin. Please create a new password to continue.
            </p>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <PasswordInput
                  value={passwordChangeData.newPassword}
                  onChange={(e) => setPasswordChangeData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password (min 6 chars)"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <PasswordInput
                  value={passwordChangeData.confirmPassword}
                  onChange={(e) => setPasswordChangeData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Changing...' : 'Change Password & Continue'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center px-6 py-16 bg-surface-50">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-crimson-gradient shadow-glow mb-4 p-2">
              <img src="/ocym-logo.png" alt="OCYM Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-surface-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>OCYM Raktadhan</h1>
            <p className="text-sm text-surface-500 mt-1" style={{ fontFamily: 'Noto Sans Devanagari, sans-serif' }}>रक्तदान</p>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-3xl font-black text-surface-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>
              Welcome back
            </h2>
            <p className="text-surface-500">Sign in to continue your journey of saving lives.</p>
          </div>

          {/* Login Type Tabs */}
          <div className="flex rounded-xl bg-surface-100 p-1 mb-6">
            <button
              type="button"
              onClick={() => setLoginType('donor')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                loginType === 'donor'
                  ? 'bg-white text-crimson-600 shadow-sm'
                  : 'text-surface-600 hover:text-surface-900'
              }`}
            >
              🩸 Donor Login
            </button>
            <button
              type="button"
              onClick={() => setLoginType('admin')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                loginType === 'admin'
                  ? 'bg-white text-crimson-600 shadow-sm'
                  : 'text-surface-600 hover:text-surface-900'
              }`}
            >
              🔐 Admin Login
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-crimson-50 border border-crimson-200 flex items-start gap-3 animate-scaleIn">
              <span className="text-crimson-500 text-lg mt-0.5">⚠️</span>
              <p className="text-crimson-800 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="email" className="field-label">Email Address</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input-field"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            {loginType === 'donor' ? (
              <div>
                <label htmlFor="phone" className="field-label">Phone Number</label>
                <input
                  id="phone"
                  type="tel"
                  required
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  className="input-field"
                  placeholder="Enter your registered phone number"
                  autoComplete="tel"
                />
                <p className="text-xs text-surface-500 mt-1.5">
                  Enter the phone number you used during registration
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="field-label mb-0">Password</label>
                  <Link to="/forgot-password" className="text-xs text-crimson-600 hover:text-crimson-700 font-medium">
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="input-field"
                />
              </div>
            )}

            <div className="flex items-center gap-2.5">
              <input
                id="remember"
                type="checkbox"
                className="w-4 h-4 rounded border-surface-300 text-crimson-600 focus:ring-crimson-500 focus:ring-offset-0"
              />
              <label htmlFor="remember" className="text-sm text-surface-600 select-none cursor-pointer">
                Keep me signed in
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-lg w-full rounded-xl mt-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Signing in…
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-surface-50 text-surface-400">New to OCYM Blood?</span>
            </div>
          </div>

          {/* Register link */}
          <Link
            to="/donor-registration"
            className="btn btn-outline btn-lg w-full rounded-xl"
          >
            ❤️ Register as a Donor
          </Link>

          {/* Security note */}
          <div className="mt-8 p-4 rounded-xl bg-sky-50 border border-sky-100 flex items-start gap-3">
            <span className="text-sky-500 text-lg mt-0.5">🔒</span>
            <div>
              <p className="text-sky-800 text-sm font-medium mb-0.5">Secured with Firebase</p>
              <p className="text-sky-600 text-xs">
                Your account is protected with industry-standard authentication. We never store plain-text passwords.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;